/**
 * Single source of truth for the ChartMind split-flap display.
 * Used by /splash/title-logos and by professional-chartmind nav so both look identical.
 * Only on very narrow viewports (CHARTMIND_NARROW_MAX px and below) use two-line "CHART" / "MIND";
 * typical iOS widths get single-line "CHARTMIND".
 */
import React from "react";
import { Box, useMediaQuery } from "@mui/material";
import { SplitFlapRow } from "../../features/facility/components/queue/splitflap/QueueDisplay";

/** Breakpoint below which we split into "CHART" / "MIND"; above this use single "CHARTMIND" (e.g. typical iOS). */
const CHARTMIND_NARROW_MAX = 380;

const DEFAULT_DISPLAY_3D = {
  perspectivePx: 520,
  perspectiveOriginYPercent: 42,
  flapTranslateZPx: 1,
  seamTranslateZPx: 2,
  capTranslateZPx: 3,
  showThicknessEdge: true,
  phaseLighting: true,
  seamHeightPx: 3,
  seamGradient:
    "linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(70,70,70,0.18) 35%, rgba(0,0,0,0.48) 100%)",
  hingeHoldMs: 18,
  charJitterMs: 1,
  edgeDarkening: true,
  edgeDarkeningStrength: 0.18,
  bottomOvershootDeg: 4,
  capWidthPx: 7,
  capHeightPx: 13,
};

const FLIP_DURATION_MS = 52;
const SPEED_MS = 46;
const CHAR_STAGGER_MS = 14;
/** Vertical offset to keep display in same position after removing frame (was marginTop + border + paddingTop) + 20px down */
const OFFSET_TOP_NO_FRAME = "calc(0.75rem + 6px + 0.9rem + 20px)";
const CHARACTER_COLOR = "common.white";
const BOARD_BG = "#0f1a2d";
/** professional-chartmind: Primary (bottom flap) */
const CHARTMIND_LOWER = "secondary.main";
/** professional-chartmind: Lighter (top flap) */
const CHARTMIND_UPPER = "#1F8DE2";
const TILE_WIDTH = "clamp(40px, 5vw, 72px)";
const TILE_HEIGHT = "clamp(52px, 6.5vw, 88px)";
/** Narrow (two-line) layout: smaller tiles. */
const TILE_WIDTH_NARROW = "clamp(18px, 4.5vw, 32px)";
const TILE_HEIGHT_NARROW = "clamp(20px, 5.2vw, 38px)";
/** AppBar nav only (~0.5× splash tiles). */
const TILE_WIDTH_NAV = "clamp(20px, 2.5vw, 36px)";
const TILE_HEIGHT_NAV = "clamp(26px, 3.25vw, 44px)";
const TILE_WIDTH_NARROW_NAV = "clamp(9px, 2.25vw, 16px)";
const TILE_HEIGHT_NARROW_NAV = "clamp(10px, 2.6vw, 19px)";

const DISPLAY_3D_NAV_SCALE = 0.5;
function getDisplay3DForNav(base) {
  return {
    ...base,
    perspectivePx: Math.round(base.perspectivePx * DISPLAY_3D_NAV_SCALE),
    seamHeightPx: Math.max(1, Math.round(base.seamHeightPx * DISPLAY_3D_NAV_SCALE)),
    capWidthPx: Math.max(3, Math.round(base.capWidthPx * DISPLAY_3D_NAV_SCALE)),
    capHeightPx: Math.max(6, Math.round(base.capHeightPx * DISPLAY_3D_NAV_SCALE)),
  };
}

const rowProps = (tileW, tileH, upper, lower) => ({
  isClock: true,
  compact: false,
  segmentBgColor: "transparent",
  segmentUpperFlapColor: upper,
  segmentLowerFlapColor: lower,
  characterColor: CHARACTER_COLOR,
  fontWeight: 700,
  charStaggerMs: CHAR_STAGGER_MS,
  boardBgColor: BOARD_BG,
  showCharacterOverlay: true,
  fontFamily: '"IBM Plex Mono", "Roboto Mono", ui-monospace, monospace',
  tileWidth: tileW,
  tileHeight: tileH,
});

/**
 * ChartMind split-flap display: frame + display + row-wrap + SplitFlapRow.
 * On very narrow (≤CHARTMIND_NARROW_MAX px): two rows "CHART" / "MIND"; else single "CHARTMIND".
 * @param {{ onRowComplete?: () => void, use3dFlip?: boolean, display3DConfig?: object, flipDurationMs?: number, flushTop?: boolean, preferNarrowAboveWidth?: number, compactNav?: boolean }} props
 *   preferNarrowAboveWidth: when set (e.g. from nav), use compact tiles for single line when viewport ≤ this so flap fits in portal; one-line vs two-line still uses CHARTMIND_NARROW_MAX.
 *   compactNav: professional-chartmind AppBar — ~0.5× tile size (more discrete than splash).
 */
function ChartMindSplitFlap({
  onRowComplete,
  onFlapComplete,
  use3dFlip = true,
  display3DConfig = DEFAULT_DISPLAY_3D,
  flipDurationMs = FLIP_DURATION_MS,
  flushTop = false,
  preferNarrowAboveWidth,
  compactNav = false,
}) {
  const narrowLayout = useMediaQuery(`(max-width:${CHARTMIND_NARROW_MAX}px)`);
  const navPortalNarrow = useMediaQuery(
    preferNarrowAboveWidth != null ? `(max-width:${preferNarrowAboveWidth}px)` : "(max-width:-1px)"
  );
  const compactSingleLine = preferNarrowAboveWidth != null && navPortalNarrow && !narrowLayout;

  const resolved3D = compactNav ? getDisplay3DForNav(display3DConfig) : display3DConfig;

  const tileW = compactNav
    ? narrowLayout || compactSingleLine
      ? TILE_WIDTH_NARROW_NAV
      : TILE_WIDTH_NAV
    : narrowLayout
      ? TILE_WIDTH_NARROW
      : compactSingleLine
        ? TILE_WIDTH_NARROW
        : TILE_WIDTH;
  const tileH = compactNav
    ? narrowLayout || compactSingleLine
      ? TILE_HEIGHT_NARROW_NAV
      : TILE_HEIGHT_NAV
    : narrowLayout
      ? TILE_HEIGHT_NARROW
      : compactSingleLine
        ? TILE_HEIGHT_NARROW
        : TILE_HEIGHT;

  const singleRow = (
    <SplitFlapRow
      textSegments={["CHARTMIND"]}
      speeds={[SPEED_MS]}
      rowIndex={0}
      flipDurationMs={flipDurationMs}
      use3dFlip={use3dFlip}
      onFlapComplete={onFlapComplete}
      onRowComplete={onRowComplete}
      display3DConfig={resolved3D}
      {...rowProps(tileW, tileH, CHARTMIND_UPPER, CHARTMIND_LOWER)}
    />
  );

  const twoRows = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: compactNav ? "1px" : "2px",
      }}
    >
      <SplitFlapRow
        textSegments={["CHART"]}
        speeds={[SPEED_MS]}
        rowIndex={0}
        flipDurationMs={flipDurationMs}
        use3dFlip={use3dFlip}
        onFlapComplete={onFlapComplete}
        display3DConfig={resolved3D}
        {...rowProps(tileW, tileH, CHARTMIND_UPPER, CHARTMIND_LOWER)}
      />
      <SplitFlapRow
        textSegments={["MIND"]}
        speeds={[SPEED_MS]}
        rowIndex={1}
        flipDurationMs={flipDurationMs}
        use3dFlip={use3dFlip}
        onFlapComplete={onFlapComplete}
        onRowComplete={onRowComplete}
        display3DConfig={resolved3D}
        {...rowProps(tileW, tileH, CHARTMIND_UPPER, CHARTMIND_LOWER)}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        marginTop: flushTop ? 0 : OFFSET_TOP_NO_FRAME,
        color: CHARACTER_COLOR,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: compactNav ? "36px" : "72px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "nowrap",
            background: "linear-gradient(180deg, #111823 0%, #0d131c 100%)",
            padding: compactNav ? "2px 3px" : "4px 6px",
            borderRadius: "4px",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.03), inset 0 -2px 4px rgba(0,0,0,0.35)",
          }}
        >
          {narrowLayout ? twoRows : singleRow}
        </Box>
      </Box>
    </Box>
  );
}

export default ChartMindSplitFlap;
